import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Badge } from "primereact/badge";
import { Message } from "primereact/message";
import Layout from "../../component/layout";
import { toastify } from "../../../../utils/toast";
import { getItem } from "../../../../utils/localStorage";
import { makeRequest2 } from "../../../../utils/makeRequest";
import {
  getUnitsForFacility,
  getCustomersURL,
  getGLAccountsURL,
  createPropertyManagerContractURL,
  get_leases,
  getUsersURL,
  GET_DEPARTMENT_URL,
  getDigitaxConfigURL
} from "../../../../utils/urls";

const CreatePropertyManagerContract = () => {
  const navigate = useNavigate();

  // Tab state
  const [activeTab, setActiveTab] = useState(0);
  const steps = [
    'Basic Information',
    'Apartment & Client',
    'Contract Terms',
    'Management Fee',
    'GL Accounts',
    'Review & Submit'
  ];

  // State management
  const [contractName, setContractName] = useState("");
  const [selectedPropertyManager, setSelectedPropertyManager] = useState("");
  const [selectedUnit, setSelectedUnit] = useState("");
  const [selectedClient, setSelectedClient] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [paymentDueDate, setPaymentDueDate] = useState("");
  const [balanceBroughtForward, setBalanceBroughtForward] = useState("0.00");
  const [managementFeeType, setManagementFeeType] = useState("percentage");
  const [managementFeeValue, setManagementFeeValue] = useState("");
  // Optional per-contract lease commission override (empty = use the
  // facility default from the Revenue Settings dialog on the revenue page)
  const [leaseFeeType, setLeaseFeeType] = useState("percentage");
  const [leaseFeeValue, setLeaseFeeValue] = useState("");
  const [frequency, setFrequency] = useState("Monthly");
  const [status, setStatus] = useState("Inactive");

  // Lease-based data state
  const [hasLeaseData, setHasLeaseData] = useState(false);
  const [existingLeases, setExistingLeases] = useState([]);
  const [unitHasLease, setUnitHasLease] = useState(false);
  const [contractCompletionStatus, setContractCompletionStatus] = useState({
    canBeActive: false,
    missingFields: [],
    hasAnyLeaseData: false
  });

  // GL Account state
  const [glAccounts, setGlAccounts] = useState([]);
  const [invoiceDebitAccount, setInvoiceDebitAccount] = useState("");
  const [invoiceCreditAccount, setInvoiceCreditAccount] = useState("");
  const [paymentDebitAccount, setPaymentDebitAccount] = useState("");
  const [paymentCreditAccount, setPaymentCreditAccount] = useState("");
  const [loadingGLAccounts, setLoadingGLAccounts] = useState(false);

  // Data lists
  const [unitsWithHomeowners, setUnitsWithHomeowners] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [propertyManagers, setPropertyManagers] = useState([]);

  // Options
  const frequencyOptions = ['Monthly', 'Quarterly', 'Annually'];
  const paymentDueDateOptions = [
    { value: "31", label: "End of month" },
    { value: "1", label: "1st of month" },
    { value: "5", label: "5th of month" },
    { value: "10", label: "10th of month" },
    { value: "15", label: "15th of month" }
  ];
  const managementFeeTypes = [
    { value: "percentage", label: "Percentage of Rent" },
    { value: "amount", label: "Fixed Amount" }
  ];

  const [digitaxEnabled, setDigitaxEnabled] = useState(false);
  const [digitaxProfileId, setDigitaxProfileId] = useState(null);
  const [digitaxProfiles, setDigitaxProfiles] = useState([]);

  const fetchDigitaxProfiles = async () => {
    try {
      const facilityId = await getItem("selectedFacilityId");
      const response = await makeRequest2(
        `${getDigitaxConfigURL}/${facilityId}`,
        "GET"
      );

      console.log("Digitax config response:", response);

      if (response.success) {
        setDigitaxProfiles(
          response.data?.digitax?.profiles || []
        );
      }
    } catch (error) {
      console.error(
        "Failed to fetch DigiTax profiles:",
        error
      );
    }
  };

  // Fetch property managers
  const fetchPropertyManagers = async () => {
    try {
      const facilityId = await getItem("selectedFacilityId");
      const response = await makeRequest2(`${getUsersURL}/${facilityId}`, "GET");

      let usersData = [];
      if (response.success && Array.isArray(response.data)) {
        usersData = response.data;
      } else if (response.success && response.data && Array.isArray(response.data.users)) {
        usersData = response.data.users;
      } else if (Array.isArray(response)) {
        usersData = response;
      }

      const propManagers = usersData.filter(user => {
        if (!user || !user.department) return false;
        if (typeof user.department === 'object' && user.department.name) {
          return user.department.name === 'Property Management';
        }
        return false;
      });

      if (propManagers.length === 0) {
        try {
          const deptResponse = await makeRequest2(`${GET_DEPARTMENT_URL}/${facilityId}`, 'GET');
          let departments = [];
          if (deptResponse.success && deptResponse.data) {
            const departmentData = deptResponse.data.data || deptResponse.data;
            departments = Array.isArray(departmentData) ? departmentData : [departmentData];
          }

          const propMgmtDept = departments.find(dept => dept.name === 'Property Management');
          if (propMgmtDept) {
            const propManagersByDeptId = usersData.filter(user => {
              if (!user || !user.department) return false;
              if (typeof user.department === 'string') {
                return user.department === propMgmtDept._id;
              }
              if (typeof user.department === 'object' && user.department._id) {
                return user.department._id === propMgmtDept._id;
              }
              return false;
            });
            setPropertyManagers(propManagersByDeptId);
          } else {
            setPropertyManagers([]);
          }
        } catch (deptError) {
          setPropertyManagers([]);
        }
      } else {
        setPropertyManagers(propManagers);
      }
    } catch (error) {
      console.error("Error fetching property managers:", error);
      setPropertyManagers([]);
    }
  };

  // Fetch apartments with homeowners
  const fetchUnitsWithHomeowners = async () => {
    try {
      const facilityId = await getItem("selectedFacilityId");
      const response = await makeRequest2(`${getUnitsForFacility}/${facilityId}`, "GET");

      let unitsData = [];
      if (response.success && Array.isArray(response.data)) {
        unitsData = response.data;
      } else if (response.success && response.data && Array.isArray(response.data.units)) {
        unitsData = response.data.units;
      } else if (Array.isArray(response)) {
        unitsData = response;
      }

      const unitsWithHomeOwners = unitsData.filter(unit =>
        unit && unit.homeOwnerId
      );
      setUnitsWithHomeowners(unitsWithHomeOwners);
    } catch (error) {
      console.error("Error fetching apartments:", error);
      setUnitsWithHomeowners([]);
    }
  };

  // Fetch customers
  const fetchCustomers = async () => {
    try {
      const facilityId = await getItem("selectedFacilityId");
      const response = await makeRequest2(`${getCustomersURL}/${facilityId}`, "GET");

      let customersData = [];
      if (response.success && Array.isArray(response.data)) {
        customersData = response.data;
      } else if (response.success && response.data && Array.isArray(response.data.customers)) {
        customersData = response.data.customers;
      } else if (Array.isArray(response)) {
        customersData = response;
      }

      const homeowners = customersData.filter(customer =>
        customer && customer.customerType === 'home owner'
      );
      setCustomers(homeowners);
    } catch (error) {
      console.error("Error fetching customers:", error);
      setCustomers([]);
    }
  };

  // Fetch lease for unit
  const fetchLeaseForUnit = async (unitId) => {
    try {
      const facilityId = await getItem("selectedFacilityId");
      const response = await makeRequest2(`${get_leases}/${facilityId}`, "GET");

      if (response.success) {
        const leases = response.data?.leaseAgreements || [];
        const relevantLease = leases.find(lease =>
          lease.unitNumber === unitId &&
          ['Active', 'Pending'].includes(lease.status)
        );

        setExistingLeases(relevantLease ? [relevantLease] : []);

        if (relevantLease) {
          setHasLeaseData(true);
          setUnitHasLease(true);
          populateFromLeaseData(relevantLease);
          updateCompletionStatus(true, relevantLease);
        } else {
          setHasLeaseData(false);
          setUnitHasLease(false);
          clearLeaseBasedFields();
          updateCompletionStatus(false);
        }
      }
    } catch (error) {
      console.error("Error fetching lease:", error);
      setHasLeaseData(false);
      setUnitHasLease(false);
    }
  };

  // Populate from lease data
  const populateFromLeaseData = (lease) => {
    if (lease.leaseTerms?.startDate) {
      setStartDate(new Date(lease.leaseTerms.startDate).toISOString().split('T')[0]);
    }
    if (lease.leaseTerms?.endDate) {
      setEndDate(new Date(lease.leaseTerms.endDate).toISOString().split('T')[0]);
    }
    if (lease.financialTerms?.paymentDueDate) {
      setPaymentDueDate(lease.financialTerms.paymentDueDate.toString());
    }
    if (lease.financialTerms?.balanceBroughtForward !== undefined) {
      setBalanceBroughtForward(lease.financialTerms.balanceBroughtForward.toString());
    }
    if (lease.billingCycle?.frequency) {
      setFrequency(lease.billingCycle.frequency);
    }
    setStatus("Active");
  };

  // Clear lease-based fields
  const clearLeaseBasedFields = () => {
    setStartDate("");
    setEndDate("");
    setPaymentDueDate("");
    setBalanceBroughtForward("0.00");
    setFrequency("Monthly");
    setStatus("Inactive");
  };

  // Update completion status
  const updateCompletionStatus = (hasLease, lease = null) => {
    let missingFields = [];
    let canBeActive = false;

    if (hasLease && lease) {
      if (!lease.leaseTerms?.startDate) missingFields.push('Start Date');
      if (!lease.leaseTerms?.endDate) missingFields.push('End Date');
      if (!lease.financialTerms?.paymentDueDate) missingFields.push('Payment Due Date');
      if (!lease.billingCycle?.frequency) missingFields.push('Frequency');
      canBeActive = missingFields.length === 0;
    } else {
      missingFields = ['Start Date', 'End Date', 'Payment Due Date', 'Frequency'];
      canBeActive = false;
    }

    setContractCompletionStatus({
      canBeActive,
      missingFields,
      hasAnyLeaseData: hasLease
    });
  };

  // Fetch GL accounts
  const fetchGLAccounts = async () => {
    try {
      setLoadingGLAccounts(true);
      const facilityId = await getItem("selectedFacilityId");
      if (!facilityId) {
        toastify("No facility selected. Please select a facility first.", "error");
        return;
      }
      const response = await makeRequest2(`${getGLAccountsURL}/${facilityId}`, "GET");
      if (response && response.success) {
        const accountsData = response.data?.data || [];
        setGlAccounts(accountsData);
      } else {
        setGlAccounts([]);
      }
    } catch (error) {
      console.error("Error in fetchGLAccounts:", error);
      setGlAccounts([]);
    } finally {
      setLoadingGLAccounts(false);
    }
  };

  // Handle unit selection
  const handleUnitSelect = async (unitId) => {
    setSelectedUnit(unitId);

    const selectedUnitObject = unitsWithHomeowners.find(unit => unit._id === unitId);
    if (selectedUnitObject) {
      const homeOwnerId = selectedUnitObject.homeOwnerId;
      setSelectedClient(homeOwnerId);
    } else {
      setSelectedClient("");
    }

    if (unitId) {
      await fetchLeaseForUnit(unitId);
    } else {
      setHasLeaseData(false);
      setExistingLeases([]);
      setUnitHasLease(false);
      clearLeaseBasedFields();
      updateCompletionStatus(false);
    }
  };

  // Check if form is valid
  const isFormValid = () => {
    if (!contractName || contractName.trim() === "") return false;
    if (!selectedPropertyManager) return false;
    if (!selectedUnit) return false;
    if (!selectedClient) return false;
    if (!managementFeeValue || isNaN(managementFeeValue) || managementFeeValue < 0) return false;
    if (managementFeeType === "percentage" && managementFeeValue > 100) return false;
    if (!leaseFeeValue || isNaN(leaseFeeValue) || leaseFeeValue < 0) return false;
    if (leaseFeeType === "percentage" && leaseFeeValue > 100) return false;
    return true;
  };

  // Handle form submission
  const handleContractSubmit = async () => {
    try {
      const facilityId = await getItem("selectedFacilityId");

      if (!contractName || contractName.trim() === "") {
        toastify("Contract name is required.", "error");
        return;
      }
      if (!selectedPropertyManager) {
        toastify("Property manager is required.", "error");
        return;
      }
      if (!selectedUnit) {
        toastify("A apartment must be selected.", "error");
        return;
      }
      if (!selectedClient) {
        toastify("A client must be selected.", "error");
        return;
      }
      if (!managementFeeValue || isNaN(managementFeeValue) || managementFeeValue < 0) {
        toastify("Management fee value must be a valid positive number.", "error");
        return;
      }
      if (managementFeeType === "percentage" && managementFeeValue > 100) {
        toastify("Management fee percentage cannot exceed 100%.", "error");
        return;
      }
      if (!leaseFeeValue || isNaN(leaseFeeValue) || leaseFeeValue < 0) {
        toastify("Lease commission value must be a valid positive number.", "error");
        return;
      }
      if (leaseFeeType === "percentage" && leaseFeeValue > 100) {
        toastify("Lease commission percentage cannot exceed 100%.", "error");
        return;
      }

      const contractData = {
        contractName,
        propertyManager: selectedPropertyManager,
        units: [selectedUnit],
        customerId: selectedClient,
        managementFee: {
          type: managementFeeType,
          value: parseFloat(managementFeeValue)
        },
        leaseFee: {
          type: leaseFeeType,
          value: parseFloat(leaseFeeValue)
        },
        digitax: {
          enabled: Boolean(digitaxEnabled),
          profileId: digitaxEnabled
            ? (digitaxProfileId || null)
            : null
        }
      };

      if (startDate) contractData.startDate = startDate;
      if (endDate) contractData.endDate = endDate;
      if (paymentDueDate) contractData.paymentDueDate = parseInt(paymentDueDate);
      if (balanceBroughtForward) contractData.balanceBroughtForward = parseFloat(balanceBroughtForward);
      if (frequency) contractData.frequency = frequency;

      if (invoiceDebitAccount && invoiceCreditAccount && paymentDebitAccount && paymentCreditAccount) {
        contractData.glAccounts = {
          invoice: {
            debit: invoiceDebitAccount,
            credit: invoiceCreditAccount
          },
          payment: {
            debit: paymentDebitAccount,
            credit: paymentCreditAccount
          }
        };
      }

      const response = await makeRequest2(
        `${createPropertyManagerContractURL}/${facilityId}`,
        "POST",
        contractData
      );

      if (response.success === true) {
        toastify("Property Manager Contract created successfully!", "success");
        handleClear();
        navigate("/facility/property_management/property_contract_management");
        return;
      }

      if (response.error && response.error.includes("201")) {
        toastify("Property Manager Contract created successfully!", "success");
        handleClear();
        navigate("/facility/property_management/property_contract_management");
        return;
      }

      throw new Error(response.error || "Failed to create contract");
    } catch (err) {
      console.error("Error creating contract:", err);
      if (err.message && err.message.includes("201")) {
        toastify("Property Manager Contract created successfully!", "success");
        handleClear();
        navigate("/facility/property_management/property_contract_management");
        return;
      }
      toastify(err.message || "An unexpected error occurred while creating the contract.", "error");
    }
  };

  // Clear form
  const handleClear = () => {
    setContractName("");
    setSelectedPropertyManager("");
    setSelectedUnit("");
    setSelectedClient("");
    setStartDate("");
    setEndDate("");
    setPaymentDueDate("");
    setBalanceBroughtForward("0.00");
    setManagementFeeType("percentage");
    setManagementFeeValue("");
    setFrequency("Monthly");
    setStatus("Inactive");
    setInvoiceDebitAccount("");
    setInvoiceCreditAccount("");
    setPaymentDebitAccount("");
    setPaymentCreditAccount("");
    setHasLeaseData(false);
    setExistingLeases([]);
    setUnitHasLease(false);
    setDigitaxEnabled(false);
    setDigitaxProfileId(null);
    setContractCompletionStatus({
      canBeActive: false,
      missingFields: [],
      hasAnyLeaseData: false
    });
    setActiveTab(0);
  };

  // Get selected client name
  const getSelectedClientName = () => {
    if (!selectedClient) return "No client selected";
    const client = customers.find(c => c._id === selectedClient);
    return client ? `${client.firstName} ${client.lastName}` : "Client not found";
  };

  // Get selected unit name
  const getSelectedUnitName = () => {
    if (!selectedUnit) return "No apartment selected";
    const unit = unitsWithHomeowners.find(u => u._id === selectedUnit);
    return unit ? `${unit.name} - ${unit.unitType} (${unit.division})` : "Apartment not found";
  };

  // Load data on mount
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        await Promise.all([
          fetchUnitsWithHomeowners(),
          fetchCustomers(),
          fetchGLAccounts(),
          fetchPropertyManagers()
        ]);
      } catch (error) {
        console.error("Error loading initial data:", error);
      }
    };
    loadInitialData();
  }, []);

  useEffect(() => {
    fetchDigitaxProfiles();
  }, []);

  // Render step indicator
  const renderSteps = () => (
    <div className="row mb-4">
      <div className="col-12">
        <div className="d-flex justify-content-between align-items-center">
          {steps.map((step, index) => (
            <div
              key={index}
              className="d-flex flex-column align-items-center flex-grow-1"
              style={{ cursor: 'pointer' }}
              onClick={() => {
                if (index <= activeTab + 1) {
                  setActiveTab(index);
                }
              }}
            >
              <div
                className={`rounded-circle d-flex align-items-center justify-content-center mb-1 ${index <= activeTab ? 'bg-primary text-white' : 'bg-light text-muted'
                  }`}
                style={{ width: '36px', height: '36px', fontSize: '14px', fontWeight: '600' }}
              >
                {index + 1}
              </div>
              <small className={`text-center ${index <= activeTab ? 'text-primary fw-bold' : 'text-muted'}`}>
                {step}
              </small>
              {index < steps.length - 1 && (
                <div
                  className={`position-absolute ${index < activeTab ? 'bg-primary' : 'bg-light'}`}
                  style={{
                    width: 'calc(100% - 40px)',
                    height: '2px',
                    left: 'calc(50% + 18px)',
                    top: '18px',
                    zIndex: -1
                  }}
                />
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // Render tab content
  const renderTabContent = () => {
    switch (activeTab) {
      case 0:
        return renderBasicInformationTab();
      case 1:
        return renderApartmentClientTab();
      case 2:
        return renderContractTermsTab();
      case 3:
        return renderManagementFeeTab();
      case 4:
        return renderGLAccountsTab();
      case 5:
        return renderReviewTab();
      default:
        return null;
    }
  };

  // Render Basic Information Tab
  const renderBasicInformationTab = () => (
    <div className="p-3">
      <div className="row">
        <div className="col-sm-6 mb-3">
          <label className="form-label fw-bold">Contract Name <span className="text-danger">*</span></label>
          <InputText
            className="form-control"
            value={contractName}
            onChange={(e) => setContractName(e.target.value)}
            placeholder="Enter contract name"
          />
          <small className="text-muted">Give this contract a unique identifier</small>
        </div>

        <div className="col-sm-6 mb-3">
          <label className="form-label fw-bold">Property Manager <span className="text-danger">*</span></label>
          <select
            className="form-select"
            value={selectedPropertyManager}
            onChange={(e) => setSelectedPropertyManager(e.target.value)}
          >
            <option value="">-- Select Property Manager --</option>
            {propertyManagers.map((manager) => (
              <option key={manager._id} value={manager._id}>
                {manager.fullName || manager.email} - {manager.email}
              </option>
            ))}
          </select>
          <small className="text-muted">
            {propertyManagers.length === 0 ? (
              <span className="text-warning">
                <i className="ti ti-alert-triangle me-1"></i>
                No property managers found. Ensure users are assigned to "Property Management" department.
              </span>
            ) : (
              "Select a property manager from the Property Management department"
            )}
          </small>
        </div>

        <div className="col-sm-6 mb-3">
          <label className="form-label fw-bold">Status</label>
          <div className="form-control bg-light" style={{ minHeight: '38px' }}>
            <Badge
              value={status}
              severity={status === 'Active' ? 'success' : status === 'Inactive' ? 'warning' : 'info'}
              className="me-2"
            />
            <small className="text-muted">
              {status === 'Active' ? 'Will be active (has lease data)' : 'Will be inactive (awaiting lease data)'}
            </small>
          </div>
          <small className="text-info">
            <i className="ti ti-info-circle me-1"></i>
            Status is automatically determined based on lease data availability
          </small>
        </div>
      </div>
    </div>
  );

  // Render Apartment & Client Tab
  const renderApartmentClientTab = () => (
    <div className="p-3">
      <div className="row">
        <div className="col-sm-6 mb-3">
          <label className="form-label fw-bold">Apartment to Manage <span className="text-danger">*</span></label>
          <select
            className="form-select"
            value={selectedUnit}
            onChange={(e) => handleUnitSelect(e.target.value)}
          >
            <option value="">-- Select Apartment --</option>
            {unitsWithHomeowners.map((unit) => (
              <option key={unit._id} value={unit._id}>
                {unit.name} - {unit.unitType} ({unit.division})
              </option>
            ))}
          </select>
          <small className="text-muted">
            {unitsWithHomeowners.length === 0 ? (
              <span className="text-warning">
                <i className="ti ti-alert-triangle me-1"></i>
                No apartments with homeowners found.
              </span>
            ) : (
              "Select an apartment to manage. Only apartments with assigned homeowners are shown."
            )}
          </small>
        </div>

        <div className="col-sm-6 mb-3">
          <label className="form-label fw-bold">Client</label>
          <div className="form-control bg-light" style={{ minHeight: '38px' }}>
            <i className="ti ti-user me-2"></i>
            {getSelectedClientName()}
          </div>
          <small className="text-info">
            <i className="ti ti-info-circle me-1"></i>
            Client is automatically selected based on apartment ownership
          </small>
        </div>

        {selectedUnit && (
          <div className="col-12 mb-3">
            <div className="row">
              {unitHasLease && (
                <div className="col-sm-6">
                  <div className="alert alert-success">
                    <i className="ti ti-check me-2"></i>
                    This apartment has an existing lease agreement and will populate contract fields automatically.
                  </div>
                </div>
              )}
              {!unitHasLease && selectedUnit && (
                <div className="col-sm-6">
                  <div className="alert alert-warning">
                    <i className="ti ti-alert-triangle me-2"></i>
                    This apartment is ready for property management but doesn't have a lease agreement yet.
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // Render Contract Terms Tab
  const renderContractTermsTab = () => (
    <div className="p-3">
      {hasLeaseData ? (
        <div className="alert alert-success">
          <i className="ti ti-check me-2"></i>
          These fields are automatically populated from existing lease data
        </div>
      ) : (
        <div className="alert alert-warning">
          <i className="ti ti-alert-triangle me-2"></i>
          These fields will be populated automatically once a lease agreement is created for the selected apartment
        </div>
      )}

      <div className="row">
        <div className="col-sm-6 mb-3">
          <label className="form-label fw-bold">Start Date</label>
          <input
            type="date"
            className="form-control"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            disabled={!hasLeaseData}
            style={hasLeaseData ? { backgroundColor: '#e9ecef' } : {}}
          />
          <small className={hasLeaseData ? "text-success" : "text-muted"}>
            {hasLeaseData ? "Populated from lease agreement" : "Will be populated when lease agreement is created"}
          </small>
        </div>

        <div className="col-sm-6 mb-3">
          <label className="form-label fw-bold">End Date</label>
          <input
            type="date"
            className="form-control"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            disabled={!hasLeaseData}
            style={hasLeaseData ? { backgroundColor: '#e9ecef' } : {}}
          />
          <small className={hasLeaseData ? "text-success" : "text-muted"}>
            {hasLeaseData ? "Populated from lease agreement" : "Will be populated when lease agreement is created"}
          </small>
        </div>

        <div className="col-sm-6 mb-3">
          <label className="form-label fw-bold">Payment Due Date</label>
          <select
            className="form-select"
            value={paymentDueDate}
            onChange={(e) => setPaymentDueDate(e.target.value)}
            disabled={!hasLeaseData}
            style={hasLeaseData ? { backgroundColor: '#e9ecef' } : {}}
          >
            <option value="">Select</option>
            {paymentDueDateOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <small className={hasLeaseData ? "text-success" : "text-muted"}>
            {hasLeaseData ? "Populated from lease payment terms" : "Will be populated when lease agreement is created"}
          </small>
        </div>

        <div className="col-sm-6 mb-3">
          <label className="form-label fw-bold">Payment Frequency</label>
          <select
            className="form-select"
            value={frequency}
            onChange={(e) => setFrequency(e.target.value)}
            disabled={!hasLeaseData}
            style={hasLeaseData ? { backgroundColor: '#e9ecef' } : {}}
          >
            {frequencyOptions.map((freq) => (
              <option key={freq} value={freq}>
                {freq}
              </option>
            ))}
          </select>
          <small className={hasLeaseData ? "text-success" : "text-muted"}>
            {hasLeaseData ? "Populated from lease billing cycle" : "Will be populated when lease agreement is created"}
          </small>
        </div>

        <div className="col-sm-6 mb-3">
          <label className="form-label fw-bold">Balance Brought Forward</label>
          <input
            type="number"
            className="form-control"
            value={balanceBroughtForward}
            onChange={(e) => setBalanceBroughtForward(e.target.value)}
            step="0.01"
            placeholder="0.00"
            disabled={!hasLeaseData}
            style={hasLeaseData ? { backgroundColor: '#e9ecef' } : {}}
          />
          <small className={hasLeaseData ? "text-success" : "text-muted"}>
            {hasLeaseData ? "Populated from lease financial terms" : "Will be populated when lease agreement is created"}
          </small>
        </div>
      </div>
    </div>
  );

  // Render Management Fee Tab
  const renderManagementFeeTab = () => (
    <div className="p-3">
      <div className="row">
        <div className="col-sm-6 mb-3">
          <label className="form-label fw-bold">Management Fee Type <span className="text-danger">*</span></label>
          <select
            className="form-select"
            value={managementFeeType}
            onChange={(e) => setManagementFeeType(e.target.value)}
          >
            {managementFeeTypes.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
        </div>

        <div className="col-sm-6 mb-3">
          <label className="form-label fw-bold">
            Management Fee Value <span className="text-danger">*</span>
          </label>
          <div className="input-group">
            <input
              type="number"
              className="form-control"
              value={managementFeeValue}
              onChange={(e) => setManagementFeeValue(e.target.value)}
              min="0"
              max={managementFeeType === "percentage" ? "100" : undefined}
              step="0.01"
              placeholder={managementFeeType === "percentage" ? "Enter percentage (0-100)" : "Enter fixed amount"}
            />
            <span className="input-group-text">
              {managementFeeType === "percentage" ? "%" : "$"}
            </span>
          </div>
          <small className="text-muted">
            {managementFeeType === "percentage"
              ? "Percentage of rent to be charged as management fee"
              : "Fixed amount to be charged as management fee"
            }
          </small>
        </div>
      </div>

      <hr className="my-4" />

      <h6 className="fw-bold mb-1">Lease Commission</h6>
      <p className="text-muted small mb-3">
        The one-time fee earned when this manager places a new tenant on a
        unit covered by this contract, paid on the first rent payment.
      </p>

      <div className="row">
        <div className="col-sm-6 mb-3">
          <label className="form-label fw-bold">
            Lease Commission Type <span className="text-danger">*</span>
          </label>
          <select
            className="form-select"
            value={leaseFeeType}
            onChange={(e) => setLeaseFeeType(e.target.value)}
          >
            {managementFeeTypes.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
        </div>
        <div className="col-sm-6 mb-3">
          <label className="form-label fw-bold">
            Lease Commission Value <span className="text-danger">*</span>
          </label>
          <div className="input-group">
            <input
              type="number"
              className="form-control"
              value={leaseFeeValue}
              onChange={(e) => setLeaseFeeValue(e.target.value)}
              min="0"
              max={leaseFeeType === "percentage" ? "100" : undefined}
              step="0.01"
              placeholder={leaseFeeType === "percentage" ? "Enter percentage (0-100)" : "Enter fixed amount"}
            />
            <span className="input-group-text">
              {leaseFeeType === "percentage" ? "%" : "KES"}
            </span>
          </div>
          <small className="text-muted">
            One-time fee earned on the first rent payment for this contract's units
          </small>
        </div>

        <div className="col-sm-6 mb-4">
          <label className="form-label">
            DigiTax Submission Profile
          </label>

          <select
            className="form-select"
            value={
              digitaxEnabled
                ? (digitaxProfileId || "legacy")
                : ""
            }
            onChange={(e) => {
              const value = e.target.value;

              if (value === "") {
                // DigiTax disabled
                setDigitaxEnabled(false);
                setDigitaxProfileId(null);
              } else if (value === "legacy") {
                // Use facility-level DigiTax configuration
                setDigitaxEnabled(true);
                setDigitaxProfileId(null);
              } else {
                // Use selected DigiTax profile
                setDigitaxEnabled(true);
                setDigitaxProfileId(value);
              }
            }}
          >
            <option value="">
              DigiTax Disabled
            </option>

            <option value="legacy">
              Use Facility DigiTax Configuration
            </option>

            {digitaxProfiles
              .filter(profile => profile.enabled)
              .map(profile => (
                <option
                  key={profile._id}
                  value={profile._id}
                >
                  {profile.name}
                </option>
              ))}
          </select>
        </div>

      </div>
    </div>
  );

  // Render GL Accounts Tab
  const renderGLAccountsTab = () => (
    <div className="p-3">
      {loadingGLAccounts && (
        <div className="alert alert-info">
          <i className="ti ti-loader me-2"></i>
          Loading GL accounts...
        </div>
      )}

      {!loadingGLAccounts && glAccounts.length === 0 && (
        <div className="alert alert-warning">
          <i className="ti ti-alert-triangle me-2"></i>
          No GL accounts found. GL accounts are optional but recommended for proper accounting.
        </div>
      )}

      {!loadingGLAccounts && glAccounts.length > 0 && (
        <>
          <div className="row">
            <div className="col-12 mb-3">
              <h6 className="fw-bold">Invoice GL Entries</h6>
              <p className="text-muted">Configure accounts for invoice generation</p>
            </div>

            <div className="col-sm-6 mb-3">
              <label className="form-label fw-bold">Invoice Debit Account</label>
              <select
                className="form-select"
                value={invoiceDebitAccount}
                onChange={(e) => setInvoiceDebitAccount(e.target.value)}
              >
                <option value="">-- Select Debit Account --</option>
                {glAccounts.map((account) => (
                  <option key={account._id} value={account._id}>
                    {account.accountCode} - {account.accountName} ({account.accountType})
                  </option>
                ))}
              </select>
              <small className="text-muted">Account to be debited when invoices are created</small>
            </div>

            <div className="col-sm-6 mb-3">
              <label className="form-label fw-bold">Invoice Credit Account</label>
              <select
                className="form-select"
                value={invoiceCreditAccount}
                onChange={(e) => setInvoiceCreditAccount(e.target.value)}
              >
                <option value="">-- Select Credit Account --</option>
                {glAccounts.map((account) => (
                  <option key={account._id} value={account._id}>
                    {account.accountCode} - {account.accountName} ({account.accountType})
                  </option>
                ))}
              </select>
              <small className="text-muted">Account to be credited when invoices are created</small>
            </div>

            <div className="col-12 mb-3">
              <h6 className="fw-bold">Payment GL Entries</h6>
              <p className="text-muted">Configure accounts for payment processing</p>
            </div>

            <div className="col-sm-6 mb-3">
              <label className="form-label fw-bold">Payment Debit Account</label>
              <select
                className="form-select"
                value={paymentDebitAccount}
                onChange={(e) => setPaymentDebitAccount(e.target.value)}
              >
                <option value="">-- Select Debit Account --</option>
                {glAccounts.map((account) => (
                  <option key={account._id} value={account._id}>
                    {account.accountCode} - {account.accountName} ({account.accountType})
                  </option>
                ))}
              </select>
              <small className="text-muted">Account to be debited when payments are received</small>
            </div>

            <div className="col-sm-6 mb-3">
              <label className="form-label fw-bold">Payment Credit Account</label>
              <select
                className="form-select"
                value={paymentCreditAccount}
                onChange={(e) => setPaymentCreditAccount(e.target.value)}
              >
                <option value="">-- Select Credit Account --</option>
                {glAccounts.map((account) => (
                  <option key={account._id} value={account._id}>
                    {account.accountCode} - {account.accountName} ({account.accountType})
                  </option>
                ))}
              </select>
              <small className="text-muted">Account to be credited when payments are received</small>
            </div>
          </div>
        </>
      )}
    </div>
  );

  // Render Review & Submit Tab
  const renderReviewTab = () => (
    <div className="p-3">
      <div className="alert alert-info">
        <i className="ti ti-info-circle me-2"></i>
        Please review all the information before submitting the contract.
      </div>

      <div className="card">
        <div className="card-body">
          <h6 className="fw-bold mb-3">Contract Summary</h6>
          <div className="row">
            <div className="col-md-6">
              <div className="mb-3">
                <label className="text-muted">Contract Name</label>
                <p className="fw-bold">{contractName || 'Not specified'}</p>
              </div>
              <div className="mb-3">
                <label className="text-muted">Property Manager</label>
                <p className="fw-bold">
                  {propertyManagers.find(m => m._id === selectedPropertyManager)?.fullName ||
                    propertyManagers.find(m => m._id === selectedPropertyManager)?.email ||
                    'Not specified'}
                </p>
              </div>
              <div className="mb-3">
                <label className="text-muted">Apartment</label>
                <p className="fw-bold">{getSelectedUnitName()}</p>
              </div>
              <div className="mb-3">
                <label className="text-muted">Client</label>
                <p className="fw-bold">{getSelectedClientName()}</p>
              </div>
            </div>
            <div className="col-md-6">
              <div className="mb-3">
                <label className="text-muted">Status</label>
                <p className="fw-bold">
                  <Badge
                    value={status}
                    severity={status === 'Active' ? 'success' : status === 'Inactive' ? 'warning' : 'info'}
                  />
                </p>
              </div>
              <div className="mb-3">
                <label className="text-muted">Management Fee</label>
                <p className="fw-bold">
                  {managementFeeType === 'percentage' ? `${managementFeeValue}%` : `$${managementFeeValue}`}
                </p>
              </div>
              {hasLeaseData && (
                <>
                  <div className="mb-3">
                    <label className="text-muted">Start Date</label>
                    <p className="fw-bold">{startDate || 'Not set'}</p>
                  </div>
                  <div className="mb-3">
                    <label className="text-muted">End Date</label>
                    <p className="fw-bold">{endDate || 'Not set'}</p>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {!isFormValid() && (
        <div className="alert alert-danger mt-3">
          <i className="ti ti-alert-circle me-2"></i>
          Please fill in all required fields before submitting.
        </div>
      )}
    </div>
  );

  return (
    <Layout>
      <div className="page-header">
        <div className="page-block">
          <div className="row align-items-center">
            <div className="col-md-12">
              <ul className="breadcrumb">
                <li className="breadcrumb-item">
                  <Link to="/facility/">Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to="/facility/property_management/property_contract_management">
                    Property Contract Management
                  </Link>
                </li>
                <li className="breadcrumb-item active">Create Property Manager Contract</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-sm-12">
          {/* Back Button */}
          <div className="card">
            <div className="card-header">
              <Link to="#" onClick={() => navigate(-1)}>
                <i className="ti ti-arrow-narrow-left"></i> Back
              </Link>
            </div>
          </div>

          {/* Main Form Card */}
          <div className="card">
            <div className="card-header">
              <h5 className="mb-0">Create Property Manager Contract</h5>

              {/* Status Alerts */}
              {hasLeaseData && unitHasLease && (
                <div className="alert alert-success mt-2 mb-0">
                  <i className="ti ti-check me-2"></i>
                  <strong>Lease Data Found:</strong> The selected apartment has an existing lease agreement. Contract fields will be auto-populated and status will be "Active".
                </div>
              )}

              {selectedUnit && !hasLeaseData && (
                <div className="alert alert-warning mt-2 mb-0">
                  <i className="ti ti-alert-triangle me-2"></i>
                  <strong>No Lease Data:</strong> Selected apartment does not have an existing lease agreement. Contract will be created with "Inactive" status and will be activated once a lease agreement is created.
                </div>
              )}

              {contractCompletionStatus.hasAnyLeaseData && contractCompletionStatus.missingFields.length > 0 && (
                <div className="alert alert-info mt-2 mb-0">
                  <i className="ti ti-info-circle me-2"></i>
                  <strong>Incomplete Lease Data:</strong> Some required fields are missing from the lease data: {contractCompletionStatus.missingFields.join(', ')}
                </div>
              )}
            </div>

            <div className="card-body">
              <div className="container-fluid">
                {/* Steps Indicator */}
                {renderSteps()}

                {/* Tab Content */}
                <div className="mt-4">
                  {renderTabContent()}
                </div>

                {/* Navigation Buttons */}
                <div className="d-flex justify-content-between align-items-center mt-4 pt-3 border-top">
                  <div>
                    <button
                      className="btn btn-secondary"
                      onClick={() => setActiveTab(activeTab - 1)}
                      disabled={activeTab === 0}
                    >
                      <i className="ti ti-arrow-left me-2"></i>
                      Previous
                    </button>
                  </div>
                  <div>
                    <button
                      className="btn btn-outline-secondary me-2"
                      onClick={handleClear}
                    >
                      <i className="ti ti-x me-2"></i>
                      Clear
                    </button>
                    {activeTab === steps.length - 1 ? (
                      <button
                        className="btn btn-primary"
                        onClick={handleContractSubmit}
                        disabled={!isFormValid()}
                      >
                        <i className="ti ti-check me-2"></i>
                        Create Contract
                      </button>
                    ) : (
                      <button
                        className="btn btn-primary"
                        onClick={() => setActiveTab(activeTab + 1)}
                      >
                        Next
                        <i className="ti ti-arrow-right ms-2"></i>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default CreatePropertyManagerContract;